import type {
  AdminCatalog,
  AdminDomain,
  AdminEditableVersion,
  AdminGame,
  AdminImportResult,
  AdminSyncStatus,
  SyncRunResponse,
  SyncRunStatus,
  SyncSchedule,
  ArchiveDomain,
  ArchiveLead,
  ArtifactPage,
  ArtifactTreePage,
  ComparePage,
  Game,
  ManualArtifactPayload,
  ManualVersionPayload,
  ProbeSchedule,
  ProbeStatus,
  VersionDetail,
  VersionSummary,
} from "./types";

const DEFAULT_API_BASE = "/api/v1";

/**
 * The API origin is a public build-time value.  Keep the same-origin default
 * so the existing FastAPI-served build and Vite proxy continue to work.
 */
export function apiBase(): string {
  const configured = import.meta.env.VITE_API_BASE_URL?.trim();
  const base = configured || DEFAULT_API_BASE;
  return base.replace(/\/+$/, "") || DEFAULT_API_BASE;
}

export function apiUrl(path: string): string {
  return `${apiBase()}/${path.replace(/^\/+/, "")}`;
}

export class ApiError extends Error {
  constructor(
    message: string,
    public readonly status: number,
    public readonly url: string,
    public readonly code = "request_failed",
    public readonly details?: unknown,
  ) {
    super(message);
    this.name = "ApiError";
  }
}

type ErrorEnvelope = { error?: { code?: string; message?: string; details?: unknown } };

export function isAbortError(error: unknown): boolean {
  return typeof error === "object" && error !== null
    && "name" in error && (error as { name?: unknown }).name === "AbortError";
}

export async function requestJson<T>(path: string, signal?: AbortSignal, init: RequestInit = {}): Promise<T> {
  const url = apiUrl(path);
  let response: Response;
  try {
    response = await fetch(url, {
      ...init,
      signal,
      headers: { Accept: "application/json", ...init.headers },
    });
  } catch (error) {
    if (isAbortError(error)) throw error;
    throw new ApiError("无法连接归档 API", 0, url, "network_error");
  }
  if (!response.ok) {
    const payload = await response.json().catch(() => null) as ErrorEnvelope | null;
    throw new ApiError(
      payload?.error?.message || `API 请求失败：HTTP ${response.status}`,
      response.status,
      url,
      payload?.error?.code || "http_error",
      payload?.error?.details,
    );
  }
  return response.json() as Promise<T>;
}

export const api = {
  games: (signal?: AbortSignal) => requestJson<Game[]>("/games", signal),
  domains: (gameId: string, signal?: AbortSignal) =>
    requestJson<ArchiveDomain[]>(`/games/${encodeURIComponent(gameId)}/domains`, signal),
  versions: async (domainId: string, signal?: AbortSignal) => {
    const response = await requestJson<{ items: VersionSummary[] }>(`/domains/${encodeURIComponent(domainId)}/versions`, signal);
    return response.items;
  },
  versionDetail: (domainId: string, version: string, signal?: AbortSignal) =>
    requestJson<VersionDetail>(`/domains/${encodeURIComponent(domainId)}/versions/${encodeURIComponent(version)}`, signal),
  leads: async (domainId: string, signal?: AbortSignal) => {
    const response = await requestJson<{ items: ArchiveLead[] }>(`/domains/${encodeURIComponent(domainId)}/leads`, signal);
    return response.items;
  },
  artifacts: (
    domainId: string,
    version: string,
    options: { cursor?: string | null; query?: string; state?: string; kind?: string; limit?: number },
    signal?: AbortSignal,
  ) => {
    const parameters = new URLSearchParams({ limit: String(options.limit || 100) });
    if (options.cursor) parameters.set("cursor", options.cursor);
    if (options.query) parameters.set("q", options.query);
    if (options.state) parameters.set("availability_state", options.state);
    if (options.kind) parameters.set("kind", options.kind);
    return requestJson<ArtifactPage>(`/domains/${encodeURIComponent(domainId)}/versions/${encodeURIComponent(version)}/artifacts?${parameters}`, signal);
  },
  allArtifacts: async (
    domainId: string,
    version: string,
    options: { query?: string; state?: string; kind?: string } = {},
    signal?: AbortSignal,
  ) => {
    const items: ArtifactPage["items"] = [];
    let cursor: string | null = null;
    do {
      const page = await api.artifacts(domainId, version, { ...options, cursor, limit: 500 }, signal);
      items.push(...page.items);
      cursor = page.next_cursor;
    } while (cursor);
    return items;
  },
  artifactTree: (
    domainId: string,
    version: string,
    options: { kind: string; prefix?: string; cursor?: string | null; limit?: number; state?: string },
    signal?: AbortSignal,
  ) => {
    const parameters = new URLSearchParams({
      kind: options.kind,
      prefix: options.prefix || "",
      limit: String(options.limit || 100),
    });
    if (options.cursor) parameters.set("cursor", options.cursor);
    if (options.state) parameters.set("availability_state", options.state);
    return requestJson<ArtifactTreePage>(`/domains/${encodeURIComponent(domainId)}/versions/${encodeURIComponent(version)}/artifact-tree?${parameters}`, signal);
  },
  compare: (
    domainId: string,
    options: {
      fromVersion: string;
      toVersion: string;
      kind?: string;
      change?: "all" | "added" | "removed" | "changed";
      limit?: number;
      cursor?: string | null;
    },
    signal?: AbortSignal,
  ) => {
    const parameters = new URLSearchParams({
      from_version: options.fromVersion,
      to_version: options.toVersion,
      change: options.change || "all",
      limit: String(options.limit || 100),
    });
    if (options.kind) parameters.set("kind", options.kind);
    if (options.cursor) parameters.set("cursor", options.cursor);
    return requestJson<ComparePage>(`/domains/${encodeURIComponent(domainId)}/compare?${parameters}`, signal);
  },
};

export const adminApi = {
  auth: (token: string) => ({ Authorization: `Bearer ${token}` }),
  catalog: (token: string, signal?: AbortSignal) =>
    requestJson<AdminCatalog>("/admin/catalog", signal, { headers: adminApi.auth(token) }),
  versions: (domainId: string, token: string, signal?: AbortSignal) =>
    requestJson<{ items: VersionSummary[] }>(`/admin/domains/${encodeURIComponent(domainId)}/versions`, signal, { headers: adminApi.auth(token) }),
  createGame: (payload: Partial<AdminGame>, token: string, signal?: AbortSignal) =>
    requestJson<AdminGame>("/admin/games", signal, { method: "POST", headers: { ...adminApi.auth(token), "Content-Type": "application/json" }, body: JSON.stringify(payload) }),
  updateGame: (gameId: string, payload: Partial<AdminGame>, token: string, signal?: AbortSignal) =>
    requestJson<AdminGame>(`/admin/games/${encodeURIComponent(gameId)}`, signal, { method: "PATCH", headers: { ...adminApi.auth(token), "Content-Type": "application/json" }, body: JSON.stringify(payload) }),
  deleteGame: (gameId: string, token: string, signal?: AbortSignal) =>
    requestJson<void>(`/admin/games/${encodeURIComponent(gameId)}`, signal, { method: "DELETE", headers: adminApi.auth(token) }),
  createDomain: (payload: Partial<AdminDomain>, token: string, signal?: AbortSignal) =>
    requestJson<AdminDomain>("/admin/domains", signal, { method: "POST", headers: { ...adminApi.auth(token), "Content-Type": "application/json" }, body: JSON.stringify(payload) }),
  updateDomain: (domainId: string, payload: Partial<AdminDomain>, token: string, signal?: AbortSignal) =>
    requestJson<AdminDomain>(`/admin/domains/${encodeURIComponent(domainId)}`, signal, { method: "PATCH", headers: { ...adminApi.auth(token), "Content-Type": "application/json" }, body: JSON.stringify(payload) }),
  deleteDomain: (domainId: string, token: string, signal?: AbortSignal) =>
    requestJson<void>(`/admin/domains/${encodeURIComponent(domainId)}`, signal, { method: "DELETE", headers: adminApi.auth(token) }),
  setVersionVisibility: (domainId: string, version: string, isVisible: boolean, token: string, signal?: AbortSignal) =>
    requestJson<{ is_visible: boolean }>(`/admin/domains/${encodeURIComponent(domainId)}/versions/${encodeURIComponent(version)}`, signal, { method: "PATCH", headers: { ...adminApi.auth(token), "Content-Type": "application/json" }, body: JSON.stringify({ is_visible: isVisible }) }),
  editableVersion: (domainId: string, version: string, token: string, signal?: AbortSignal) =>
    requestJson<AdminEditableVersion>(`/admin/domains/${encodeURIComponent(domainId)}/versions/${encodeURIComponent(version)}/editable`, signal, { headers: adminApi.auth(token) }),
  editArtifact: (domainId: string, version: string, payload: { action: "upsert" | "delete"; part: number; artifact?: ManualArtifactPayload | null }, token: string, signal?: AbortSignal) =>
    requestJson<AdminImportResult>(`/admin/domains/${encodeURIComponent(domainId)}/versions/${encodeURIComponent(version)}/artifacts/edit`, signal, { method: "POST", headers: { ...adminApi.auth(token), "Content-Type": "application/json" }, body: JSON.stringify(payload) }),
  addVersion: (domainId: string, payload: ManualVersionPayload, token: string, signal?: AbortSignal) =>
    requestJson<AdminImportResult>(`/admin/domains/${encodeURIComponent(domainId)}/versions`, signal, { method: "POST", headers: { ...adminApi.auth(token), "Content-Type": "application/json" }, body: JSON.stringify(payload) }),
  syncStatus: (token: string, signal?: AbortSignal) =>
    requestJson<AdminSyncStatus>("/admin/sync-status", signal, { headers: adminApi.auth(token) }),
  syncRun: (token: string, signal?: AbortSignal) =>
    requestJson<SyncRunResponse>("/admin/sync/run", signal, { method: "POST", headers: adminApi.auth(token) }),
  syncRunStatus: (token: string, signal?: AbortSignal) =>
    requestJson<SyncRunStatus>("/admin/sync/status", signal, { headers: adminApi.auth(token) }),
  syncSchedule: (token: string, signal?: AbortSignal) =>
    requestJson<SyncSchedule>("/admin/sync/schedule", signal, { headers: adminApi.auth(token) }),
  saveSyncSchedule: (schedule: SyncSchedule, token: string, signal?: AbortSignal) =>
    requestJson<SyncSchedule>("/admin/sync/schedule", signal, {
      method: "PUT", headers: adminApi.auth(token), body: JSON.stringify(schedule),
    }),
  probeStatus: (token: string, signal?: AbortSignal) =>
    requestJson<ProbeStatus>("/admin/probe/status", signal, { headers: adminApi.auth(token) }),
  probeRun: (mode: "normal" | "full", token: string, signal?: AbortSignal) =>
    requestJson<{ status: string; log_path?: string }>("/admin/probe/run", signal, { method: "POST", headers: { ...adminApi.auth(token), "Content-Type": "application/json" }, body: JSON.stringify({ mode, family: "android" }) }),
  probeSchedule: (token: string, signal?: AbortSignal) =>
    requestJson<ProbeSchedule>("/admin/probe/schedule", signal, { headers: adminApi.auth(token) }),
  saveProbeSchedule: (payload: ProbeSchedule, token: string, signal?: AbortSignal) =>
    requestJson<ProbeSchedule>("/admin/probe/schedule", signal, { method: "PUT", headers: { ...adminApi.auth(token), "Content-Type": "application/json" }, body: JSON.stringify(payload) }),
};
